/* eslint-disable @typescript-eslint/no-unused-vars */
import { createAsyncThunk } from '@reduxjs/toolkit';
import createHttpClient from 'store/httpClient';
import { fromObjectToQueryString } from 'utils/api';
import { stockEndPoints } from 'api/stock/constants';
import {
  TStockCreateBE,
  TStockCreateBody,
  TStockGetListBE,
  TStockGetListByIdsBE,
  TStockGetListByIdsQueryParams,
  TStockGetListByTypeBE,
  TStockGetListByTypeQueryParams,
  TStockGetListQueryParams,
  TStockItemBE,
  TStockReservationAbortBE,
  TStockReservationAbortBody,
  TStockReservationCreateBE,
  TStockReservationCreateBody,
  TStockUpdateBE,
  TStockUpdateBody,
} from 'api/stock/stock.types';

/** Set `REACT_APP_MOCK_STOCK_LIST=false` to call the real API for stock list */
const USE_MOCK_STOCK_LIST = process.env.REACT_APP_MOCK_STOCK_LIST !== 'false';

function parseStockListPayload(raw: unknown): TStockGetListBE['data'] | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const o = raw as Record<string, unknown>;
  if (Array.isArray(o.items)) {
    return { items: o.items as TStockGetListBE['data']['items'] };
  }
  const inner = o.data;
  if (
    inner &&
    typeof inner === 'object' &&
    Array.isArray((inner as TStockGetListBE['data']).items)
  ) {
    return inner as TStockGetListBE['data'];
  }
  return null;
}

export const getStockListAsyncThunk = createAsyncThunk<
  TStockGetListBE['data'],
  { queryParams: TStockGetListQueryParams } | void
>('stock/getList', async (args, { rejectWithValue }) => {
  try {
    const httpClient = createHttpClient(undefined);

    const response = await httpClient.get<TStockGetListBE>(
      stockEndPoints.getList(
        fromObjectToQueryString(
          typeof args === 'object' ? args?.queryParams : {}
        )
      )
    );

    return response.data;
  } catch (_) {
    return rejectWithValue(null);
  }
});

export const getStockListByTypeAsyncThunk = createAsyncThunk<
  TStockGetListByTypeBE['data'],
  { queryParams: TStockGetListByTypeQueryParams } | void
>('stock/getList', async (args, { rejectWithValue }) => {
  try {
    const httpClient = createHttpClient(undefined);

    const response = await httpClient.get<TStockGetListByTypeBE>(
      stockEndPoints.getListByType(
        fromObjectToQueryString(
          typeof args === 'object' ? args?.queryParams : {}
        )
      )
    );

    return response.data;
  } catch (_) {
    return rejectWithValue(null);
  }
});

export const getStockListByIdsAsyncThunk = createAsyncThunk<
  TStockGetListByIdsBE['data'],
  {
    queryParams: TStockGetListByIdsQueryParams;
    onSuccess?: (stockItems: TStockItemBE[]) => void;
  } | void
>('stock/getListByIds', async (args, { rejectWithValue }) => {
  try {
    const httpClient = createHttpClient(undefined);

    const response = await httpClient.get<TStockGetListByIdsBE>(
      stockEndPoints.getListByIds(
        fromObjectToQueryString(
          typeof args === 'object' ? args?.queryParams : {}
        )
      )
    );
    if (typeof args === 'object' && args?.onSuccess) {
      args.onSuccess(response.data?.items ?? []);
    }
    return response.data;
  } catch (_) {
    return rejectWithValue(null);
  }
});

export const createStockAsyncThunk = createAsyncThunk<
  TStockCreateBE['data'],
  { body: TStockCreateBody; onSuccess?: () => void }
>('stock/create', async ({ body, onSuccess }, { rejectWithValue }) => {
  try {
    const httpClient = createHttpClient(undefined);
    const response = await httpClient.post<TStockCreateBE, TStockCreateBody>(
      stockEndPoints.create(),
      body
    );

    if (onSuccess) {
      onSuccess();
    }

    return response.data;
  } catch (_) {
    return rejectWithValue(null);
  }
});

export const updateStockAsyncThunk = createAsyncThunk<
  TStockUpdateBE['data'],
  { id: string; body: TStockUpdateBody; onSuccess?: () => void }
>('stock/update', async ({ id, body, onSuccess }, { rejectWithValue }) => {
  try {
    const httpClient = createHttpClient(undefined);
    const response = await httpClient.put<TStockUpdateBE, TStockUpdateBody>(
      stockEndPoints.update(id),
      body
    );

    if (onSuccess) {
      onSuccess();
    }

    return response.data;
  } catch (_) {
    return rejectWithValue(null);
  }
});

export const createStockReservationAsyncThunk = createAsyncThunk<
  TStockReservationCreateBE['data'],
  {
    body: TStockReservationCreateBody;
    onSuccess?: (reservationId: string) => void;
  }
>(
  'stock/reservation/create',
  async ({ body, onSuccess }, { rejectWithValue }) => {
    try {
      const httpClient = createHttpClient(undefined);
      const response = await httpClient.post<
        TStockReservationCreateBE,
        TStockReservationCreateBody
      >(stockEndPoints.reservationCreate(), body);

      if (onSuccess && response.data?.id) {
        onSuccess(response.data.id);
      }

      return response.data;
    } catch (_) {
      return rejectWithValue(null);
    }
  }
);

export const abortStockReservationAsyncThunk = createAsyncThunk<
  TStockReservationAbortBE['data'],
  { body: TStockReservationAbortBody; onSuccess?: () => void }
>(
  'stock/reservation/abort',
  async ({ body, onSuccess }, { rejectWithValue }) => {
    try {
      const httpClient = createHttpClient(undefined);
      const response = await httpClient.post<
        TStockReservationAbortBE,
        TStockReservationAbortBody
      >(stockEndPoints.reservationAbort(), body);

      if (onSuccess) {
        onSuccess();
      }

      return response.data;
    } catch (_) {
      return rejectWithValue(null);
    }
  }
);
